import type {UseQueryResult} from '@tanstack/react-query'
import {fromJust} from '@freckle/maybe'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import type {components} from '@freckle/educator-entities/ts/curricula-api/generated-client'
import sortFreckleTextLevels from '@freckle/educator-entities/ts/ela/articles/helpers/sort-freckle-text-levels'
import {curriculaClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'

export type ElaArticleInfoAttrs = components['schemas']['ElaArticleBrief']

function getFreckleTextLevelForArticleInfo(info: ElaArticleInfoAttrs, uuid: string): string {
  const level = getArticleLevelForArticleInfo(info, uuid)
  return level.freckleTextLevel
}

function getArticleLevelForArticleInfo(
  info: ElaArticleInfoAttrs,
  uuid: string,
): components['schemas']['ElaArticleLevelBrief'] {
  const articleLevels = info.levels
  return fromJust(
    articleLevels.find(level => level.id === uuid),
    `Could not find reading level for article level id ${uuid}`,
  )
}

export function getFreckleTextLevelForArticleInfoCollection(
  elaArticleInfoCollection: Array<ElaArticleInfoAttrs>,
  articleUuid: string,
  elaArticleLevelId: string,
): string {
  const articleInfo = fromJust(
    elaArticleInfoCollection.find(elaArticleInfo => elaArticleInfo.uuid === articleUuid),
    `Could not find article info with uuid ${articleUuid}`,
  )
  return getFreckleTextLevelForArticleInfo(articleInfo, elaArticleLevelId)
}

export function getLevelsSortedByFreckleTextLevel(
  elaArticleInfo: ElaArticleInfoAttrs,
): Array<components['schemas']['ElaArticleLevelBrief']> {
  return elaArticleInfo.levels.sort(sortFreckleTextLevels)
}

export async function fetchElaArticleInfoCollection(): Promise<ElaArticleInfoAttrs[]> {
  return curriculaClient.GET('/ela-article-info.json').then(extractData)
}

export const useElaArticleInfo = (): UseQueryResult<ElaArticleInfoAttrs[]> =>
  useAsync(fetchElaArticleInfoCollection, 'fetchElaArticleInfoCollection', [])
